/**
 * Contact Form Section
 * - Scroll-in reveal for heading/form (IntersectionObserver, respects reduced motion)
 * - Adds .has-value so underline state persists after blur when a field is filled
 * - Progressively enhances the native Shopify {% form 'contact' %} with a
 *   no-reload submit: posts to the real /contact endpoint, follows Shopify's
 *   redirect, and swaps in the success/error markup Shopify renders back into
 *   this same section — no custom backend, no fake endpoints.
 */
(function () {
  'use strict';

  var sections = document.querySelectorAll('[data-section-type="contact-form"]');
  if (!sections.length) return;

  sections.forEach(initSection);

  function initSection(section) {
    initReveal(section);
    initFieldStates(section);
    initSubmit(section);
  }

  function initReveal(section) {
    var targets = section.querySelectorAll('[data-cf-reveal]');
    if (!targets.length) return;

    if (
      !('IntersectionObserver' in window) ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      targets.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }

    var delay = 0;
    var observer = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var el = entry.target;
          var thisDelay = delay;
          delay += 90;
          setTimeout(function () { el.classList.add('is-visible'); }, thisDelay);
          obs.unobserve(el);
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
    );

    targets.forEach(function (el) { observer.observe(el); });
  }

  function initFieldStates(section) {
    var fields = section.querySelectorAll('.contact-form__field');

    fields.forEach(function (field) {
      var control = field.querySelector('input, select, textarea');
      if (!control) return;

      var sync = function () {
        var hasValue = !!control.value && control.value.trim() !== '';
        field.classList.toggle('has-value', hasValue);
      };

      sync();
      control.addEventListener('input', sync);
      control.addEventListener('change', sync);
      control.addEventListener('blur', sync);
    });

    // Auto-grow the message textarea instead of a fixed tall box (keeps the
    // editorial single-line-per-field rhythm until the visitor actually types).
    var textarea = section.querySelector('.contact-form__field--textarea textarea');
    if (textarea) {
      var grow = function () {
        textarea.style.height = 'auto';
        textarea.style.height = Math.max(textarea.scrollHeight, 32) + 'px';
      };
      textarea.addEventListener('input', grow);
      grow();
    }
  }

  function initSubmit(section) {
    var form = section.querySelector('.contact-form');
    if (!form) return;

    var successEl = form.querySelector('[data-cf-success]');
    var errorEl = form.querySelector('[data-cf-errors]');

    form.addEventListener('submit', function (event) {
      // If fetch isn't available, let the browser do a normal full-page submit.
      if (typeof window.fetch !== 'function') return;

      event.preventDefault();
      if (form.classList.contains('is-submitting')) return;
      form.classList.add('is-submitting');

      var formData = new FormData(form);

      fetch(form.action, {
        method: 'POST',
        body: formData,
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      })
        .then(function (response) { return response.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var freshSuccess = doc.querySelector('[data-cf-success]');
          var freshError = doc.querySelector('[data-cf-errors]');

          var posted = freshSuccess && !freshSuccess.hasAttribute('hidden');
          var hasErrors = freshError && !freshError.hasAttribute('hidden');

          if (successEl) {
            successEl.innerHTML = freshSuccess ? freshSuccess.innerHTML : successEl.innerHTML;
            successEl.hidden = !posted;
          }
          if (errorEl) {
            errorEl.innerHTML = freshError ? freshError.innerHTML : errorEl.innerHTML;
            errorEl.hidden = !hasErrors;
          }

          if (posted) {
            form.reset();
            section.querySelectorAll('.contact-form__field').forEach(function (f) {
              f.classList.remove('has-value');
            });
            if (successEl) successEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
          } else if (hasErrors && errorEl) {
            errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
          } else {
            // Unexpected response shape: fail safe with a real submit.
            form.submit();
          }
        })
        .catch(function () {
          // Network/parse failure: fall back to a normal submit so the
          // visitor's message is never silently lost.
          form.submit();
        })
        .finally(function () {
          form.classList.remove('is-submitting');
        });
    });
  }
})();